import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/life-insurance-for-self-employed-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

type SEAge = '25' | '30' | '35' | '40' | '45' | '50';
type SECoverage = '250k' | '500k' | '1m' | '2m';
type SEHealth = 'excellent' | 'good' | 'fair';

interface SEResult {
  carrier: string;
  amBest: string;
  type: string;
  monthlyRate: number;
  rateClass: string;
  approvalTime: string;
  selfEmployedNote: string;
  taxDeductible: boolean;
  badge?: string;
}

const SE_BASE: Record<SEAge, Record<SECoverage, number>> = {
  '25': { '250k': 14, '500k': 23, '1m': 39, '2m': 72 },
  '30': { '250k': 19, '500k': 28, '1m': 48, '2m': 89 },
  '35': { '250k': 24, '500k': 36, '1m': 64, '2m': 119 },
  '40': { '250k': 36, '500k': 55, '1m': 97, '2m': 181 },
  '45': { '250k': 57, '500k': 88, '1m': 153, '2m': 285 },
  '50': { '250k': 90, '500k': 139, '1m': 245, '2m': 457 },
};

const HEALTH_MULT: Record<SEHealth, number> = {
  excellent: 0.88,
  good: 1.0,
  fair: 1.32,
};

function getSEResults(age: SEAge, coverage: SECoverage, health: SEHealth): SEResult[] {
  const base = SE_BASE[age][coverage];
  const hm = HEALTH_MULT[health];
  const m = Math.round(base * hm);
  return [
    {
      carrier: 'Banner Life',
      amBest: 'A+',
      type: '20-Year Term',
      monthlyRate: m,
      rateClass: health === 'excellent' ? 'Preferred Plus' : health === 'good' ? 'Preferred' : 'Standard Plus',
      approvalTime: '24–48 hrs',
      selfEmployedNote: 'Best rates for self-employed Indianapolis professionals. No employer verification required. Accelerated underwriting — no exam up to $1M.',
      taxDeductible: true,
      badge: 'Best for Self-Employed',
    },
    {
      carrier: 'Protective Life',
      amBest: 'A+',
      type: '20-Year Term',
      monthlyRate: Math.round(m * 1.04),
      rateClass: health === 'excellent' ? 'Preferred Plus' : health === 'good' ? 'Preferred' : 'Standard Plus',
      approvalTime: '24–72 hrs',
      selfEmployedNote: 'Excellent for Indianapolis freelancers and contractors. No group plan required. Strong conversion rights for growing businesses.',
      taxDeductible: true,
    },
    {
      carrier: 'Pacific Life',
      amBest: 'A+',
      type: '20-Year Term',
      monthlyRate: Math.round(m * 1.07),
      rateClass: health === 'excellent' ? 'Preferred Plus' : health === 'good' ? 'Preferred' : 'Standard',
      approvalTime: '1–2 weeks',
      selfEmployedNote: 'Top pick for Indianapolis business owners needing $1M+ coverage. Competitive rates for high-income self-employed professionals.',
      taxDeductible: true,
    },
    {
      carrier: 'Prudential',
      amBest: 'A+',
      type: '20-Year Term',
      monthlyRate: Math.round(m * 1.10),
      rateClass: health === 'excellent' ? 'Preferred' : health === 'good' ? 'Standard Plus' : 'Standard',
      approvalTime: '1–3 weeks',
      selfEmployedNote: 'Best for Indianapolis self-employed with complex health histories. Lenient underwriting for controlled conditions.',
      taxDeductible: true,
    },
    {
      carrier: 'Mutual of Omaha',
      amBest: 'A+',
      type: '20-Year Term',
      monthlyRate: Math.round(m * 1.12),
      rateClass: health === 'excellent' ? 'Preferred' : health === 'good' ? 'Standard Plus' : 'Standard',
      approvalTime: '1–2 weeks',
      selfEmployedNote: 'Strong for Indianapolis sole proprietors and LLCs. Good key person insurance options for small business owners.',
      taxDeductible: true,
    },
  ];
}

const SE_BENEFITS = [
  {
    title: 'No employer group plan — you need private coverage',
    desc: 'Self-employed Indianapolis residents don\'t have access to employer-sponsored group life insurance. Private life insurance is your only option — and it\'s often better: portable, customizable, and not tied to any employer.',
    icon: 'ri-briefcase-line',
  },
  {
    title: 'Life insurance premiums may be tax-deductible',
    desc: 'Self-employed Indianapolis residents may be able to deduct life insurance premiums as a business expense — especially for key person insurance or buy-sell agreement policies. Consult your CPA for your specific situation.',
    icon: 'ri-money-dollar-circle-line',
  },
  {
    title: 'Protect your business with key person insurance',
    desc: 'If your Indianapolis business depends on you, key person insurance protects your business if you die. The business owns the policy and receives the death benefit — providing funds to hire a replacement, pay off debts, or wind down operations.',
    icon: 'ri-building-line',
  },
  {
    title: 'Fund buy-sell agreements with life insurance',
    desc: 'If you have a business partner in Indianapolis, a buy-sell agreement funded by life insurance ensures your partner can buy your share of the business if you die — protecting both families and the business.',
    icon: 'ri-links-line',
  },
];

const CLUSTER_LINKS = [
  { to: '/term-life-insurance-quotes-indianapolis', label: 'Term Life Insurance Quotes Indianapolis', icon: 'ri-calculator-line' },
  { to: '/business-life-insurance-indianapolis', label: 'Business Life Insurance Indianapolis', icon: 'ri-building-line' },
  { to: '/life-insurance-rates-indianapolis', label: 'Life Insurance Rates Indianapolis', icon: 'ri-line-chart-line' },
  { to: '/affordable-life-insurance-indianapolis', label: 'Affordable Life Insurance Indianapolis', icon: 'ri-money-dollar-circle-line' },
  { to: '/no-exam-life-insurance-indianapolis', label: 'No Exam Life Insurance Indianapolis', icon: 'ri-stethoscope-line' },
  { to: '/best-life-insurance-indianapolis', label: 'Best Life Insurance Indianapolis', icon: 'ri-award-line' },
  { to: '/cheap-life-insurance-indianapolis', label: 'Cheap Life Insurance Indianapolis', icon: 'ri-price-tag-3-line' },
  { to: '/life-insurance-agent-indianapolis', label: 'Life Insurance Agent Indianapolis', icon: 'ri-user-star-line' },
  { to: '/life-insurance-quotes-indianapolis', label: 'Life Insurance Quotes Indianapolis', icon: 'ri-search-line' },
  { to: '/group-life-insurance-indianapolis', label: 'Group Life Insurance Indianapolis', icon: 'ri-team-line' },
  { to: '/term-life-insurance-indianapolis', label: 'Term Life Insurance Indianapolis', icon: 'ri-calendar-check-line' },
  { to: '/life-insurance-for-families-indianapolis', label: 'Life Insurance for Families Indianapolis', icon: 'ri-home-heart-line' },
];

const FAQS = [
  {
    q: 'How do self-employed Indianapolis residents get life insurance?',
    a: 'Self-employed Indianapolis residents get life insurance the same way as employees — by applying directly with a carrier or through an independent agent. The key difference: you don\'t have access to employer-sponsored group life insurance, so you need to buy private coverage. Here\'s how to get life insurance as a self-employed Indianapolis resident: 1. Work with an independent agent who compares 20+ carriers simultaneously. 2. Choose the right coverage amount — typically 10–12x your annual income. 3. Select the right policy type — term life for income replacement, whole life or IUL for wealth building. 4. Apply online or by phone — many carriers offer accelerated underwriting with no medical exam. 5. Get approved in 24–48 hours (accelerated underwriting) or 1–3 weeks (traditional underwriting). Call (317) 648-2983 for a free self-employed life insurance consultation.',
  },
  {
    q: 'Is life insurance tax-deductible for self-employed Indianapolis residents?',
    a: 'Life insurance premiums are generally NOT tax-deductible for self-employed Indianapolis residents when the policy is for personal coverage. However, there are important exceptions: Key person insurance: If your Indianapolis business owns a life insurance policy on a key employee (including yourself), the premiums may be deductible as a business expense. Buy-sell agreement insurance: Premiums for policies funding a buy-sell agreement may be deductible. S-Corp or C-Corp: If your Indianapolis business is structured as an S-Corp or C-Corp, the business may be able to deduct premiums for group life insurance provided to employees. The bottom line: consult your CPA or tax advisor for your specific situation. An independent agent can help you structure your life insurance to maximize tax benefits for your Indianapolis business.',
  },
  {
    q: 'How much life insurance do self-employed Indianapolis residents need?',
    a: 'Self-employed Indianapolis residents typically need more life insurance than employees because: 1. No employer group life insurance to supplement. 2. Business debts and obligations may need to be paid off. 3. Business partners may need to buy out your share. 4. Your income may be more variable — requiring more coverage to account for fluctuations. Recommended coverage for self-employed Indianapolis residents: Personal coverage: 10–12x annual income. Plus mortgage balance. Plus business debts. Plus education costs for children. Business coverage: Key person insurance equal to 1–2x annual revenue. Buy-sell agreement coverage equal to your share of the business value. For a self-employed Indianapolis professional earning $100,000/year with a $250,000 mortgage and $500,000 business value: Personal coverage: $1,000,000–$1,200,000. Business coverage: $500,000–$1,000,000. Total: $1,500,000–$2,200,000.',
  },
  {
    q: 'What is key person life insurance for Indianapolis businesses?',
    a: 'Key person life insurance (also called key man insurance) is a life insurance policy owned by an Indianapolis business on a key employee — typically the owner, founder, or a critical employee whose death would significantly impact the business. How it works: The Indianapolis business pays the premiums. The business is the beneficiary. If the key person dies, the business receives the death benefit. The business uses the funds to: hire and train a replacement, pay off business debts, buy out the deceased\'s share of the business, or wind down operations in an orderly manner. Cost: Key person insurance typically costs $50–$200/month for $500K–$1M in coverage, depending on the key person\'s age and health. Tax treatment: Key person insurance premiums are generally NOT tax-deductible, but the death benefit is received tax-free by the business. Call (317) 648-2983 to get a key person insurance quote for your Indianapolis business.',
  },
  {
    q: 'What is a buy-sell agreement and how does life insurance fund it?',
    a: 'A buy-sell agreement is a legally binding contract between Indianapolis business partners that determines what happens to each partner\'s share of the business if one partner dies, becomes disabled, or wants to exit the business. Life insurance funds the buy-sell agreement by providing the surviving partner with the cash needed to buy out the deceased partner\'s share. How it works: Each Indianapolis business partner takes out a life insurance policy on the other partner. If one partner dies, the surviving partner receives the death benefit. The surviving partner uses the death benefit to buy the deceased partner\'s share from their estate. The deceased partner\'s family receives fair market value for their share. Without a buy-sell agreement funded by life insurance, the surviving partner may be forced to take on the deceased partner\'s family as a new business partner — or sell the business to pay the estate. Call (317) 648-2983 to set up a buy-sell agreement for your Indianapolis business.',
  },
  {
    q: 'Can self-employed Indianapolis residents get life insurance without a medical exam?',
    a: 'Yes — self-employed Indianapolis residents can get life insurance without a medical exam through accelerated underwriting. Here\'s how it works: Accelerated underwriting: Available from most major carriers for coverage up to $1M–$3M. Uses data sources (prescription history, MIB records, driving records) instead of a medical exam. Approval in 24–48 hours. Available for healthy applicants under age 60. Simplified issue: No medical exam, limited health questions. Available for coverage up to $50K–$100K. Approval in 24–48 hours. Available for applicants with some health conditions. Guaranteed issue: No medical exam, no health questions. Available for coverage up to $25K. Approval in 15–30 minutes. Available for all applicants ages 45–85. For most self-employed Indianapolis residents in good health, accelerated underwriting provides the fastest approval with no medical exam. Call (317) 648-2983 to apply.',
  },
];

const TESTIMONIALS = [
  {
    name: 'David K.',
    location: 'Indianapolis, IN (Freelance Developer)',
    quote: 'No employer benefits, no group plan. Benjamin found me $1M 20-year term for $64/month. Applied online, approved in 48 hours, no medical exam. My family is protected.',
    stars: 5,
    coverage: '$1M 20-year term',
    rate: '$64/month at age 35',
  },
  {
    name: 'Jennifer & Mark R.',
    location: 'Carmel, Indianapolis (Business Partners)',
    quote: 'We own a business together and needed a buy-sell agreement. Benjamin set up $500K policies on each of us for $36/month each. Now if one of us dies, the other can buy out the business.',
    stars: 5,
    coverage: '$500K each — buy-sell agreement',
    rate: '$36/month each at age 35',
  },
  {
    name: 'Amanda S.',
    location: 'Fishers, Indianapolis (Consultant)',
    quote: 'Self-employed consultant, no group benefits. Benjamin found me $750K for $52/month. Also set up key person insurance for my LLC. Everything handled in one call.',
    stars: 5,
    coverage: '$750K personal + key person',
    rate: '$52/month at age 38',
  },
  {
    name: 'Chris T.',
    location: 'Noblesville, Indianapolis (Contractor)',
    quote: 'Construction contractor, worried about getting coverage. Benjamin found me Banner Life at Preferred rates — $55/month for $500K. No medical exam, approved in 24 hours.',
    stars: 5,
    coverage: '$500K 20-year term',
    rate: '$55/month at age 40',
  },
];

export default function LifeInsuranceForSelfEmployedIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [seAge, setSEAge] = useState<SEAge>('35');
  const [seCoverage, setSECoverage] = useState<SECoverage>('500k');
  const [seHealth, setSEHealth] = useState<SEHealth>('good');
  const [results, setResults] = useState<SEResult[] | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Life Insurance for Self-Employed Indianapolis IN | Freelancers & Business Owners | Hoosier Life Insurance';
    const description = 'Life insurance for self-employed Indianapolis residents. Freelancers, contractors, business owners. Key person insurance, buy-sell agreements. No group plan needed. Call (317) 648-2983.';
    const keywords = 'life insurance self-employed Indianapolis, life insurance freelancer Indianapolis, key person insurance Indianapolis, buy-sell agreement life insurance Indianapolis';
    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/7ec4e22ef53c27a9129dc690da6f1536.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-12T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Life Insurance Indianapolis', item: `${SITE_URL}/life-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Life Insurance for Self-Employed Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const ageOptions: SEAge[] = ['25', '30', '35', '40', '45', '50'];
  const coverageOptions: { value: SECoverage; label: string }[] = [
    { value: '250k', label: '$250K' }, { value: '500k', label: '$500K' },
    { value: '1m', label: '$1M' }, { value: '2m', label: '$2M' },
  ];
  const healthOptions: { value: SEHealth; label: string; desc: string }[] = [
    { value: 'excellent', label: 'Excellent', desc: 'No conditions, healthy weight' },
    { value: 'good', label: 'Good', desc: 'Minor conditions, controlled' },
    { value: 'fair', label: 'Fair', desc: 'Some health conditions' },
  ];

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/life-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">Life Insurance Indianapolis</Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Life Insurance for Self-Employed</span>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20self-employed%20entrepreneur%20freelancer%20working%20from%20home%20office%20laptop%20confident%20smiling%20warm%20natural%20light%20modern%20home%20office%20cream%20beige%20tones%20financial%20security%20life%20insurance%20photorealistic%20professional%20photography%20wide%20angle&width=1440&height=700&seq=selfemployed-li-indy-hero-2025a&orientation=landscape"
            alt="Life Insurance for Self-Employed Indianapolis Indiana — Freelancers and Business Owners"
            title="Life Insurance for Self-Employed in Indianapolis, Indiana"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-briefcase-line text-sm"></i>
              Indianapolis Freelancers &amp; Business Owners · Key Person Insurance · Buy-Sell Agreements · No Group Plan Needed
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Life Insurance for Self-Employed —{' '}
              <span className="text-[#D97642]">1099 &amp; Freelancers in Indianapolis</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              No employer group plan? No problem. Use our <strong className="text-white">Self-Employed Rate Finder</strong> to compare private life insurance rates — plus key person insurance and buy-sell agreement options for Indianapolis business owners.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '$23/mo', label: 'Lowest life insurance rate for self-employed Indianapolis residents' },
                { value: 'No Exam', label: 'Accelerated underwriting — no medical exam up to $1M' },
                { value: 'Key Person', label: 'Key person insurance available for Indianapolis businesses' },
                { value: 'Buy-Sell', label: 'Buy-sell agreement funding for Indianapolis business partners' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="#se-finder" className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-briefcase-line text-lg"></i>
                Find My Self-Employed Rate
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '$23/mo', label: 'Lowest rate for self-employed Indianapolis residents — healthy 25-year-old, $250K, 20-year term' },
              { value: 'No Exam', label: 'Accelerated underwriting — no medical exam up to $1M for healthy applicants' },
              { value: 'Key Person', label: 'Key person insurance protects your Indianapolis business if you die' },
              { value: 'Buy-Sell', label: 'Buy-sell agreement funding ensures business continuity for Indianapolis partners' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">
          <div>

            {/* Why Self-Employed Need Life Insurance */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Why Self-Employed Need Life Insurance</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                4 Reasons Indianapolis Self-Employed Residents Need Life Insurance
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {SE_BENEFITS.map((benefit, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                        <i className={`${benefit.icon} text-base text-[#D97642]`}></i>
                      </div>
                      <div>
                        <span className="text-sm font-bold text-[#1A3A52] block mb-1">{benefit.title}</span>
                        <p className="text-xs text-gray-500 leading-relaxed">{benefit.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* SELF-EMPLOYED RATE FINDER */}
            <div id="se-finder" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Self-Employed Rate Finder</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Compare Life Insurance Rates — Indianapolis Self-Employed
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Select your age, coverage amount, and health to see rates from the top 5 carriers for Indianapolis self-employed residents.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-briefcase-line text-[#D97642]"></i>
                    Your Self-Employed Profile
                  </h3>

                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Your Age</label>
                    <div className="flex flex-wrap gap-2">
                      {ageOptions.map(a => (
                        <button key={a} onClick={() => { setSEAge(a); setResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${seAge === a ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-5">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Coverage Amount</label>
                    <div className="flex flex-wrap gap-2">
                      {coverageOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setSECoverage(opt.value); setResults(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${seCoverage === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Overall Health</label>
                    <div className="grid grid-cols-3 gap-3">
                      {healthOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setSEHealth(opt.value); setResults(null); }}
                          className={`px-4 py-3 rounded-xl text-left transition-all cursor-pointer ${seHealth === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          <div className="text-sm font-bold">{opt.label}</div>
                          <div className={`text-xs mt-0.5 ${seHealth === opt.value ? 'text-white/80' : 'text-white/50'}`}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button onClick={() => setResults(getSEResults(seAge, seCoverage, seHealth))}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base">
                    <i className="ri-briefcase-line text-lg"></i>
                    Find My Self-Employed Rate
                  </button>
                </div>

                {results !== null && (
                  <div className="px-7 py-7">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                      <h3 className="text-base font-bold text-[#1A3A52]">
                        Self-Employed Rates — Age {seAge} · {seHealth.charAt(0).toUpperCase() + seHealth.slice(1)} Health
                      </h3>
                      <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] whitespace-nowrap">
                        From ${Math.min(...results.map(r => r.monthlyRate))}/mo
                      </span>
                    </div>
                    <div className="flex flex-col gap-4 mb-6">
                      {results.map((r, i) => (
                        <div key={r.carrier} className={`rounded-xl border p-5 ${i === 0 ? 'border-[#4A7C59]/30 bg-[#F0FFF4]' : 'border-gray-100 bg-gray-50'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-sm font-bold flex-shrink-0 ${i === 0 ? 'bg-[#4A7C59]' : 'bg-[#1A3A52]/30'}`}>{i + 1}</div>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-bold text-[#1A3A52]">{r.carrier}</span>
                                  {r.badge && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#4A7C59] text-white whitespace-nowrap">{r.badge}</span>}
                                  <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {r.amBest}</span>
                                </div>
                                <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{r.selfEmployedNote}</p>
                                <div className="flex items-center gap-3 mt-1 flex-wrap">
                                  <span className="text-xs text-gray-400">Rate class: {r.rateClass}</span>
                                  <span className="text-xs text-gray-400">Approval: {r.approvalTime}</span>
                                  {r.taxDeductible && <span className="text-xs text-[#4A7C59] font-semibold">May be tax-deductible (consult CPA)</span>}
                                </div>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className={`text-xl font-extrabold ${i === 0 ? 'text-[#4A7C59]' : 'text-[#1A3A52]'}`}>${r.monthlyRate}<span className="text-sm font-normal">/mo</span></div>
                              <div className="text-xs text-gray-400">${(r.monthlyRate * 12).toLocaleString()}/yr</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => setShowBookingModal(true)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                      <i className="ri-calendar-check-line text-lg"></i>
                      Lock In My Rate — Free 15-Min Call
                    </button>
                  </div>
                )}

                {results === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-briefcase-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your age, coverage, and health above, then click <strong className="text-[#1A3A52]">Find My Self-Employed Rate</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Self-Employed Residents Who Got Protected
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                      <span className="bg-[#4A7C59]/10 text-[#4A7C59] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.rate}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>)}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Linking Cluster */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Indianapolis Life Insurance Guide</span>
              <h2 className="text-2xl font-bold text-[#1A3A52] mb-4 leading-tight">Explore All Indianapolis Life Insurance Topics</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {CLUSTER_LINKS.map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-4 py-3.5 bg-white rounded-xl border border-gray-100 hover:border-[#D97642]/30 hover:bg-[#FFF8F4] transition-all group cursor-pointer">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-[#1A3A52]">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Life Insurance for Self-Employed Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug"><a id={`faq-se-indy-${i}`}>{faq.q}</a></h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-briefcase-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Self-Employed Rate Finder</h3>
                    <p className="text-xs text-white/70">20+ carriers · Key person available</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold">
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                </div>
              </div>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free Self-Employed Rate Call</h3>
                    <p className="text-xs text-white/70">Key person &amp; buy-sell available</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Free Self-Employed Rate Call',
                    step0Title: 'Pick a Date', step1Title: 'Choose a Time', step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date', selectTime: 'See Available Times →', reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev', nextMonth: 'Next', noSlotsAvailable: 'No slots available',
                    selectedDateTime: 'Your Appointment', modifyTime: 'Change',
                    customerName: 'Full Name', customerPhone: 'Phone Number',
                    notes: 'Business type or coverage questions? (Optional)',
                    submitButton: 'Confirm My Free Rate Call', submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.', backButton: '← Back to Calendar', required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Self-employed Indianapolis residents deserve the same protection as everyone else
          </h2>
          <p className="text-base text-white/75 mb-7">
            No employer group plan? No problem. Private life insurance is portable, customizable, and often cheaper than group coverage. Find your rate today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#se-finder" className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-briefcase-line text-lg"></i>
              Find My Self-Employed Rate
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={152} productName="Life Insurance for Self-Employed Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Self-Employed Life Insurance" quoteHref="/instant-quote" />

      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Life Insurance for Self-Employed — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance" style={{ width: '100%', height: '600px', border: 'none' }} title="Book Appointment" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
